import {
  EntityType,
  ExactMatchQueryOperators,
  FileQueryOperators,
  MatchQueryOperators,
  QueryOperator,
  VqlIssuesValues,
  VqlMatchOps,
  VqlModeOps,
  VqlStrOps,
  VqlQuery,
  VqlQueryBase,
  FilterType,
  FilterKey,
  DateTimeFilterOp,
  APIQueryOperators,
} from "types";
import { v4 as uuidv4 } from "uuid";
import {
  LABELS_PARAMETER,
  USER_TAGS_PARAMETER,
} from "helpers/constants/miscellaneous";

const matchQueryOperatorToAPIQueryOperators: Record<
  MatchQueryOperators,
  VqlMatchOps
> = {
  [QueryOperator.IS]: "is",
  [QueryOperator.IS_NOT]: "is_not",
  [QueryOperator.IS_ONE_OF]: "one_of",
  [QueryOperator.IS_NOT_ONE_OF]: "not_one_of",
};

const exactMatchQueryOperatorToAPIQueryModes: Record<
  ExactMatchQueryOperators,
  VqlModeOps
> = {
  [QueryOperator.IS]: "in",
  [QueryOperator.IS_NOT]: "out",
};

const fileQueryOperatorToAPIQueryOperators: Record<
  FileQueryOperators,
  VqlStrOps | "one_of"
> = {
  [QueryOperator.IS_ONE_OF]: "one_of",
  [QueryOperator.CONTAINS]: "contains",
  [QueryOperator.STARTS_WITH]: "starts_with",
  [QueryOperator.ENDS_WITH]: "ends_with",
};

export const createAndUpdateLabelsFilter = (
  labels: string[],
  operator: MatchQueryOperators,
  id?: string,
): VqlQuery => ({
  id: id || uuidv4(),
  labels: {
    op: matchQueryOperatorToAPIQueryOperators[operator] as VqlMatchOps,
    value: labels,
  },
});

export const createAndUpdateMediaStatusFilter = (
  mediaStatusValue: string,
  operator: MatchQueryOperators,
  id?: string,
): VqlQuery => ({
  id: id || uuidv4(),
  media_status: {
    op: "is",
    value: mediaStatusValue,
  },
});

export const createAndUpdateIssueTypesFilter = (
  issueType: Omit<VqlIssuesValues, "duplicates">,
  operator: ExactMatchQueryOperators = QueryOperator.IS,
  confidenceRange: [number, number] = [0, 1],
  id?: string,
): VqlQuery => ({
  id: id || uuidv4(),
  issues: {
    op: "issue",
    value: issueType.toLowerCase() as VqlIssuesValues,
    confidence_min: confidenceRange[0],
    confidence_max: confidenceRange[1],
    mode: exactMatchQueryOperatorToAPIQueryModes[operator] as VqlModeOps,
  },
});

export const createAndUpdateDuplicatesFilter = (
  threshold: number,
  id?: string,
): VqlQuery => ({
  id: id || uuidv4(),
  duplicates: {
    op: "duplicates",
    value: threshold,
  },
});

const getApiQueryOperatorByQueryOperators: Record<
  QueryOperator,
  APIQueryOperators
> = {
  [QueryOperator.IS]: "is",
  [QueryOperator.IS_NOT]: "is_not",
  [QueryOperator.IS_ONE_OF]: "one_of",
  [QueryOperator.IS_NOT_ONE_OF]: "not_one_of",
  [QueryOperator.CONTAINS]: "contains",
  [QueryOperator.GREATER_THAN]: "gt",
  [QueryOperator.LESS_THAN]: "lt",
  [QueryOperator.GREATER_THAN_OR_EQUAL_TO]: "gte",
  [QueryOperator.LESS_THAN_OR_EQUAL_TO]: "lte",
  [QueryOperator.BETWEEN]: "between",
  [QueryOperator.ENDS_WITH]: "ends_with",
  [QueryOperator.STARTS_WITH]: "starts_with",
  [QueryOperator.DOES_NOT_CONTAIN]: "does_not_contain",
  [QueryOperator.EQUAL]: "eq",
};

export const createAndUpdateDateFilter = (
  value: string | [string, string],
  operator: QueryOperator,
  id?: string,
): VqlQuery => ({
  id: id ?? uuidv4(),
  created_at: {
    op: getApiQueryOperatorByQueryOperators[operator] as DateTimeFilterOp,
    value: value,
  },
});

export const createAndUpdateUniquenessFilter = (
  threshold: number,
  id?: string,
): VqlQuery => ({
  id: id || uuidv4(),
  uniqueness: { op: "uniqueness", value: threshold },
});

export const createAndUpdateUserTagsFilter = (
  userTags: string[],
  operator: MatchQueryOperators,
  id?: string,
): VqlQuery => ({
  id: id || uuidv4(),
  tags: {
    op: matchQueryOperatorToAPIQueryOperators[operator] as VqlMatchOps,
    value: userTags,
  },
});

export const createAndUpdateSearchTextFilter = (
  searchText: string,
  id?: string,
  negation?: boolean,
): VqlQuery => ({
  id: id || uuidv4(),
  text: {
    op: "semantic",
    value: searchText,
    ...(negation !== undefined && { negation }),
  },
});

export const createAndUpdateVisualSimilarityFilter = (
  referenceImageId: string,
  boundingBox?: [number, number, number, number],
  id?: string,
  operation: "media" | "upload" = "media",
  negation?: boolean,
): VqlQuery => ({
  id: id || uuidv4(),
  similarity: {
    op: operation,
    value: referenceImageId,
    ...(boundingBox && { bounding_box: boundingBox }),
    ...(negation !== undefined && { negation }),
  },
});

export const createAndUpdateEntityTypeFilter = (
  entityType: EntityType,
  id?: string,
): VqlQuery => ({
  id: id || uuidv4(),
  entity_type: {
    op: "is",
    value: entityType,
  },
});

export const createAndUpdateFileFilter = (
  file: string | string[],
  operator: FileQueryOperators,
  id?: string,
): VqlQuery => ({
  id: id || uuidv4(),
  file: {
    op: fileQueryOperatorToAPIQueryOperators[operator] as
      | VqlMatchOps
      | VqlStrOps,
    value: file,
  },
});

export const createAndUpdateFolderFilter = (
  folder: string | string[],
  operator: FileQueryOperators,
  id?: string,
): VqlQuery => ({
  id: id || uuidv4(),
  folder: {
    op: fileQueryOperatorToAPIQueryOperators[operator] as
      | VqlMatchOps
      | VqlStrOps,
    value: folder,
  },
});

export function extractValuesFromVqlQueries(
  vqlQueries: VqlQuery[],
): Record<string, string> {
  const extractedValues: Record<string, string> = {};

  vqlQueries.forEach((query) => {
    // Extract labels with "is" operator - only if there are actual values
    if (
      "labels" in query &&
      query.labels &&
      query.labels.op === "is" &&
      query.labels.value.length > 0
    ) {
      extractedValues[LABELS_PARAMETER] = JSON.stringify(query.labels.value);
    }

    // Extract tags with "is" operator - only if there are actual values
    if (
      "tags" in query &&
      query.tags &&
      query.tags.op === "is" &&
      query.tags.value.length > 0
    ) {
      extractedValues[USER_TAGS_PARAMETER] = JSON.stringify(query.tags.value);
    }
  });

  return extractedValues;
}

export const isVqlQuery = <K extends FilterKey>(
  query: VqlQuery,
  key: K,
): query is VqlQueryBase & Extract<FilterType, Record<K, any>> => {
  return key in query;
};

export const getQueryValuesForFilter = <K extends FilterKey>(
  editQueryId: string | undefined,
  oldQuery: VqlQuery | null,
  selectedOperator: QueryOperator,
  queryOperator: QueryOperator,
  existingQuery: VqlQuery | null,
  key: K,
): string[] => {
  // Case 1: Editing & operator unchanged → use old query values
  if (editQueryId && oldQuery && selectedOperator === queryOperator) {
    return ((oldQuery as any)[key]?.value as string[]) || [];
  }

  // Case 2: Creating new & existing query matches → use existing query values
  if (!editQueryId && existingQuery && isVqlQuery(existingQuery, key)) {
    return ((existingQuery as any)[key]?.value as string[]) || [];
  }

  // Case 3: Editing & operator changed → fallback to existing query values
  if (editQueryId && existingQuery && isVqlQuery(existingQuery, key)) {
    return ((existingQuery as any)[key]?.value as string[]) || [];
  }

  // Default
  return [];
};
